import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { TeaLogo } from "@/components/TeaLogo";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/consent`;
const err = (e) => e?.response?.data?.detail || "Something went wrong. Try again.";

/** Public page the applicant opens from the texted link to sign Section 7 consent. */
export default function ConsentPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/${token}`).then((r) => setD(r.data)).catch((e) => setError(err(e))); }, [token]);
  const sign = async () => {
    setBusy(true); setError("");
    try { const r = await axios.post(`${API}/${token}`, { typed_name: name, agree }); setD({ ...d, status: "signed", signed_at_local: r.data.signed_at_local }); } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="consent-page" className="min-h-screen bg-[#07050d] text-white px-5 py-10">
      <div className="max-w-xl mx-auto space-y-6">
        <TeaLogo size={48} testid="consent-logo" />
        {!d && !error && <p className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>}
        {error && !d && <p data-testid="consent-error" className="text-red-300">{error}</p>}
        {d && (d.status === "signed" ? (
          <div data-testid="consent-done" className="space-y-3"><CheckCircle2 className="h-10 w-10 text-emerald-300" /><h1 className="text-3xl font-semibold">Thank you, {d.first_name}.</h1>
            <p className="text-white/70">Your consent was signed on {d.signed_at_local}. Your referring agent and The Exchange Alliance team will be in touch.</p></div>
        ) : (
          <div className="space-y-5">
            <div><div className="text-[11px] uppercase tracking-[0.3em] text-[#d4a017]">Referral consent</div><h1 className="text-3xl sm:text-4xl font-semibold mt-1">Hi {d.first_name}, please read and sign.</h1>
              <p className="text-sm text-white/60 mt-2">{d.agent ? `${d.agent} ` : "Your agent "}{d.agency ? `at ${d.agency} ` : ""}referred you to The Exchange Alliance.</p></div>
            <div className="space-y-3 text-sm leading-relaxed text-white/80 p-4 rounded-lg border border-white/10 bg-white/[0.03]" data-testid="consent-text">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#d4a017]"><ShieldCheck className="h-4 w-4" /> Authorization for release of information</div>
              {d.consent_text.map((t) => <p key={t.slice(0, 20)}>{t}</p>)}
            </div>
            <label className="block space-y-1.5"><span className="text-xs uppercase tracking-[0.2em] text-white/50">Type your full legal name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="consent-name" className="w-full rounded-md bg-black/40 border border-white/15 focus:border-[#d4a017] outline-none px-3 py-3 text-base" /></label>
            <label className="flex items-start gap-2.5 text-sm text-white/80 cursor-pointer"><Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="consent-agree" className="mt-0.5" />I have read this, I agree, and typing my name is my signature.</label>
            {error && <p data-testid="consent-sign-error" className="text-sm text-red-300">{error}</p>}
            <button type="button" disabled={busy || !agree || name.trim().length < 2} onClick={sign} data-testid="consent-sign" className="w-full rounded-md bg-[#d4a017] text-black font-semibold py-3.5 disabled:opacity-40">{busy ? "Signing…" : "Sign consent"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
